import { z } from 'zod'

import {
  mailConnectionSchema,
  mailMessageSchema,
  mailOAuthStartSchema,
  mailPageSchema,
  mailProvidersSchema,
  type ImapConnectRequest,
  type MailConnection,
  type MailMessage,
  type MailPage,
  type MailProvider,
  type MailProviders,
  type MailScope,
} from '@/types/api/mail'

import { apiClient } from './client'

export async function getMailProviders(): Promise<MailProviders> {
  const response = await apiClient.get('/mail/providers')
  return mailProvidersSchema.parse(response.data)
}

export async function getMailConnections(): Promise<MailConnection[]> {
  const response = await apiClient.get('/mail/connections')
  return z.array(mailConnectionSchema).parse(response.data)
}

/** Checks the IMAP and SMTP logins on the server before saving; a wrong login comes back as the error. */
export async function connectImapMailbox(request: ImapConnectRequest): Promise<MailConnection> {
  const response = await apiClient.post('/mail/connections/imap', request, { suppressErrorToast: true })
  return mailConnectionSchema.parse(response.data)
}

export async function deleteMailConnection(id: string): Promise<void> {
  await apiClient.delete(`/mail/connections/${id}`)
}

/** Returns the provider's consent URL; the browser has to go there. */
export async function startMailOAuth(provider: MailProvider, scope: MailScope): Promise<string> {
  const response = await apiClient.post(`/mail/oauth/${provider}/start`, null, { params: { scope } })
  return mailOAuthStartSchema.parse(response.data).authorizationUrl
}

export async function completeMailOAuth(code: string, state: string): Promise<MailConnection> {
  const response = await apiClient.post('/mail/oauth/callback', { code, state }, { suppressErrorToast: true })
  return mailConnectionSchema.parse(response.data)
}

export async function searchMail(connectionId: string, q: string, pageToken?: string): Promise<MailPage> {
  const response = await apiClient.get(`/mail/connections/${connectionId}/messages`, {
    params: { q: q || undefined, pageToken },
  })
  return mailPageSchema.parse(response.data)
}

export async function getMailThread(connectionId: string, threadId: string): Promise<MailMessage[]> {
  const response = await apiClient.get(`/mail/connections/${connectionId}/thread`, { params: { threadId } })
  return z.array(mailMessageSchema).parse(response.data)
}
